import { useState, useRef, useEffect } from 'react';
import { initializeMap } from '../MapInitializer';

// Hook to manage map initialization and state
export const useMapState = () => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapError, setMapError] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const sourcesReadyRef = useRef(false);
  const cleanupFnRef = useRef(null);
  
  // Track component mount status
  const isMountedRef = useRef(true);
  
  // Track component lifecycle
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Set up the map
  useEffect(() => {
    // Clean up existing map first
    if (map.current) {

      if (cleanupFnRef.current) {
        cleanupFnRef.current();
      }
      map.current.remove();
      map.current = null;
      setMapReady(false);
      sourcesReadyRef.current = false;
    }
    
    // Check if component is still mounted
    if (!isMountedRef.current) {
      return;
    }
    
    // Make sure container is ready
    if (!mapContainer.current) {
      return;
    }

    const mapInitResult = initializeMap({
      mapContainer,
      setMapError,
      setMapReady,
      sourcesReadyRef,
      shouldShowDurationLabels: true
    });
    
    if (mapInitResult) {

      map.current = mapInitResult.map;
      const resizeObserver = mapInitResult.resizeObserver;
      cleanupFnRef.current = mapInitResult.cleanup;
      
      return () => {

        if (mapContainer.current) {
          resizeObserver.unobserve(mapContainer.current);
        }
        
        if (cleanupFnRef.current) {
          cleanupFnRef.current();
        }
        
        if (map.current) {
          try {
            map.current.remove();
          } catch (error) {
            console.error('Error removing map:', error);
          }
          map.current = null;
          setMapReady(false);
          sourcesReadyRef.current = false;
        }
      };
    } else {

      setMapError('Failed to initialize map');
    }
  }, []);

  return {
    mapContainer,
    map,
    mapError,
    mapReady,
    sourcesReadyRef,
    isMountedRef
  };
}; 